import { useEffect, useCallback, useRef } from 'react'
import isEqual from 'lodash/isEqual'
import { useDispatch } from 'react-redux'
import { useDeepCompareEffect } from 'react-use'
import { IDatasetQueryResponse } from '@global/api/types'
import { api } from 'api'
import { DataContainer } from 'dataContainer/data-container'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import * as DatasetActions from '../datasets.actions'
import { makeDatasetFromQueryData, getCellComments, DatasetValue } from '../utils'

interface DatasetRow {
  cvid: string
  dataset_values: DatasetValue[]
}

interface Dataset {
  commitRef?: object
  dataset_rows: DatasetRow[]
  uuid?: string
  id: string
  link_validation_results?: any[]
}

interface SubmissionTemplate {
  transformedColumns: unknown[]
}

interface Deps {
  dataset: Dataset
  submissionTemplate: SubmissionTemplate
}

interface HookArgs {
  dsId: string
  setDatasetCallback: (args: {
    dataset: any
    subTitle: string
    submissionName: string
    templateId: string
  }) => void
  setPendingRowsCallback?: (args: { dataContainer: DataContainer; dataset: Dataset }) => void
  enablePendingRows?: boolean
}

interface PendingRowsResponse {
  rows: any[]
}

interface MetadataResponse {
  template_name: string
  identifier: string
  template_id: string
  uuid: string
}

interface DatasetQueryPayload {
  commitRef?: object
  includeComments: boolean
}

/**
 * A hook to manage the dispatching to store dataset, metadata, and dataset query data.
 * @param args Hook arguments
 * @param {function} setPendingRowsCallback A callback passing metadata to be set by the parent container.
 * @param deps Dependencies for the useEffect calls, triggering re-renders.
 */
export const useSubmissionStoreEffect = (
  {
    dsId,
    setDatasetCallback,
    setPendingRowsCallback = undefined,
    enablePendingRows = true,
  }: HookArgs,
  deps: Deps,
) => {
  const url = `/api/core/v1/datasets/${dsId}/query`
  const depsRef = useRef<Deps | undefined>(undefined)

  // useEffect ref equalify check and set
  if (!depsRef.current || !isEqual(deps, depsRef.current)) {
    depsRef.current = deps
  }

  const { submissionTemplate, dataset } = depsRef.current
  const dispatch = useDispatch()

  const fetchDatasetData = useCallback(() => {
    const payload: DatasetQueryPayload = {
      ...(dataset?.commitRef && { commitRef: dataset?.commitRef }),
      includeComments: true,
    }

    return api
      .post<DatasetQueryPayload, { data: IDatasetQueryResponse }>(url, payload)
      .then(({ data }) => {
        const transformedData = makeDatasetFromQueryData(data)
        return transformedData
      })
  }, [dataset?.commitRef, dsId])

  useEffect(() => {
    if (!dsId) return

    const fetchData = async () => {
      UrlAudits.set([])

      const { data: metaDataData } = await api.get<MetadataResponse>(
        `/api/v1/account/dataset/${dsId}/metadata`,
      )
      const dsData = await fetchDatasetData()
      const { template_name, identifier, template_id } = metaDataData

      UrlAudits.clearQueue()
      UrlAudits.set(dsData?.link_validation_results)

      setDatasetCallback({
        dataset: dsData,
        subTitle: template_name,
        submissionName: identifier,
        templateId: template_id,
      })

      dispatch(DatasetActions.setDatasetMetadata(metaDataData))
      dispatch(DatasetActions.getTemplateDependencies(metaDataData?.template_id))
    }

    void fetchData()

    return () => {
      dispatch(DatasetActions.resetStore())
    }
  }, [dsId])

  /**
   * Why does setting pending rows require a new instantiation of the DataContainer?
   * - Are templateFields different once pending_rows is loaded?
   * - TODO: Describe pending rows and their purpose
   */
  useDeepCompareEffect(() => {
    if (!setPendingRowsCallback) return
    if (submissionTemplate?.transformedColumns && dataset?.dataset_rows) {
      if (!dataset?.dataset_rows || !dsId) return

      const fetchData = async () => {
        const { data: pendingRowsData } = await api.get<PendingRowsResponse>(
          `/api/v1/account/dataset/${dataset.id}/data/pending_rows`,
        )
        const { data: metaDataData } = await api.get<MetadataResponse>(
          `/api/v1/account/dataset/${dsId}/metadata`,
        )
        const datasetValues = dataset.dataset_rows.flatMap(dsRow =>
          dsRow.dataset_values.flatMap(dsValue => ({
            ...dsValue,
            cvid: dsRow.cvid,
          })),
        )

        const cellComments = getCellComments(datasetValues)
        const pendingRows = pendingRowsData.rows

        const dc = new DataContainer(
          submissionTemplate?.transformedColumns,
          { resubmit: true },
          new Set(pendingRows),
          cellComments,
        )

        const datasetUuid = dataset.uuid || metaDataData.uuid
        dc.datasetUuid = datasetUuid
        UrlAudits.pollPending(datasetUuid, dc)
        UrlAudits.validateUncompleted(dataset, submissionTemplate, dc)

        setPendingRowsCallback({
          dataContainer: dc,
          dataset,
        })
      }

      void fetchData()
    }
  }, [submissionTemplate, dataset, enablePendingRows])
}
